import { useState, useCallback, useEffect } from 'react';
import store from 'store';

export class AiApiError extends Error {
	constructor(
		message: string,
		public status: number,
		public body: Record<string, unknown>,
		public retryAfter: string | null,
	) {
		super(message);
		this.name = 'AiApiError';
	}
}

function getHeaders(): HeadersInit {
	const token = store.get('jwt');
	return {
		'Content-Type': 'application/json',
		...(token ? { Authorization: `JWT ${token}` } : {}),
	};
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
	const res = await fetch(url, {
		...options,
		credentials: 'include',
		headers: { ...getHeaders(), ...(options?.headers || {}) },
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new AiApiError(
			typeof body?.error === 'string' ? body.error : `Request failed (${res.status})`,
			res.status,
			body && typeof body === 'object' ? body : {},
			res.headers.get('Retry-After'),
		);
	}

	return res.json();
}

export function useAiApi<T>(url: string | null) {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refetch = useCallback(async () => {
		if (!url) return;
		setLoading(true);
		setError(null);
		try {
			const result = await apiFetch<T>(url);
			setData(result);
		} catch (err: any) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [url]);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { data, loading, error, refetch };
}

export async function aiGet<T>(url: string): Promise<T> {
	return apiFetch<T>(url);
}

export async function aiPost<T>(url: string, body: any, init?: RequestInit): Promise<T> {
	return apiFetch<T>(url, {
		...init,
		method: 'POST',
		body: JSON.stringify(body),
	});
}

/** `init`: extra fetch options, e.g. `keepalive` for a save sent as the page closes, or headers. */
export async function aiPut<T>(url: string, body: any, init?: RequestInit): Promise<T> {
	return apiFetch<T>(url, {
		...init,
		method: 'PUT',
		body: JSON.stringify(body),
	});
}

export async function aiDelete<T>(url: string, init?: RequestInit): Promise<T> {
	return apiFetch<T>(url, { ...init, method: 'DELETE' });
}
